/**
 * HtmlPart — renders a sanitised HTML page part. DOMPurify runs on every
 * render as defence-in-depth; the server-side sanitiser is the source of
 * truth (see backend/src/services/sanitize.ts).
 */

import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import type { HtmlPartConfig } from '@/services/api';

const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'div', 'span', 'section', 'article',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'em', 'b', 'i', 'u', 's',
  'ul', 'ol', 'li',
  'a', 'img', 'figure', 'figcaption',
  'blockquote', 'code', 'pre',
  'iframe',
];

const ALLOWED_ATTR = [
  'href', 'name', 'target', 'rel',
  'src', 'alt', 'title', 'width', 'height',
  'allow', 'allowfullscreen', 'frameborder',
  'class', 'id', 'style',
];

const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

const CONTAINER_CLASSES: Record<NonNullable<HtmlPartConfig['container']>, string> = {
  narrow: 'max-w-2xl mx-auto px-4',
  wide: 'max-w-5xl mx-auto px-4',
  full: 'w-full px-4',
};

export function HtmlPart({ config }: { config: HtmlPartConfig }) {
  const cleanHtml = useMemo(() => {
    return DOMPurify.sanitize(config.html ?? '', {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOWED_URI_REGEXP,
    });
  }, [config.html]);

  const container = config.container ?? 'wide';
  return (
    <div className={`page-html-part ${CONTAINER_CLASSES[container]}`}>
      <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: cleanHtml }} />
    </div>
  );
}

export default HtmlPart;
